import axios from '@/plugins/http/axios.js';
import config from "@/views/trivia/config/trivia_api.js";
import PathUri from '@/utils/path_uri';

const _pathUri = new PathUri(config.port, config.context);

function buildUrl(idGroup, path) {
    return `${config.baseUrl + _pathUri.generateUri() + idGroup}${path}`;
}

function headers(idEmpresa, idUsuario) {
    return {
        'idEmpresa': idEmpresa,
        'idUsuario': idUsuario
    };
}

export const service = {
    getListTrivias: (idGroup, idEmpresa, idUsuario, params = {}) => {
        const query = new URLSearchParams();
        if (params.page !== undefined) query.append('page', params.page);
        if (params.size !== undefined) query.append('size', params.size);
        if (params.status) query.append('status', params.status);
        if (params.title) query.append('title', params.title);

        const queryString = query.toString();
        const url = buildUrl(idGroup, `/trivia/core${queryString ? '?' + queryString : ''}`);

        return axios({
            method: 'get',
            url,
            headers: headers(idEmpresa, idUsuario)
        });
    },

    getTriviaById: (idGroup, idEmpresa, idUsuario, triviaId) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, `/trivia/core/${triviaId}`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    getConfig: (idGroup, idEmpresa, idUsuario) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, '/trivia/core/config'),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    putConfig: (idGroup, idEmpresa, idUsuario, data) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, '/trivia/core/config'),
            headers: { ...headers(idEmpresa, idUsuario), 'Content-Type': 'application/json' },
            data
        });
    },

    postCreateTrivia: (idGroup, idEmpresa, idUsuario, data) => {
        return axios({
            method: 'post',
            url: buildUrl(idGroup, '/trivia/core'),
            headers: { ...headers(idEmpresa, idUsuario), 'Content-Type': 'application/json' },
            data
        });
    },

    putUpdateTrivia: (idGroup, idEmpresa, idUsuario, triviaId, data) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/trivia/core/${triviaId}`),
            headers: { ...headers(idEmpresa, idUsuario), 'Content-Type': 'application/json' },
            data
        });
    },

    patchTriviaStatus: (idGroup, idEmpresa, idUsuario, triviaId, status) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/trivia/core/${triviaId}/status`),
            headers: { ...headers(idEmpresa, idUsuario), 'Content-Type': 'application/json' },
            data: { status }
        });
    },

    deleteTrivia: (idGroup, idEmpresa, idUsuario, triviaId) => {
        return axios({
            method: 'delete',
            url: buildUrl(idGroup, `/trivia/core/${triviaId}`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    postCreateQuestion: (idGroup, idEmpresa, idUsuario, triviaId, data) => {
        return axios({
            method: 'post',
            url: buildUrl(idGroup, `/trivia/core/${triviaId}/questions`),
            headers: { ...headers(idEmpresa, idUsuario), 'Content-Type': 'application/json' },
            data
        });
    },

    putUpdateQuestion: (idGroup, idEmpresa, idUsuario, questionId, data) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/trivia/core/questions/${questionId}`),
            headers: { ...headers(idEmpresa, idUsuario), 'Content-Type': 'application/json' },
            data
        });
    },

    deleteQuestion: (idGroup, idEmpresa, idUsuario, questionId) => {
        return axios({
            method: 'delete',
            url: buildUrl(idGroup, `/trivia/core/questions/${questionId}`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    putReorderQuestions: (idGroup, idEmpresa, idUsuario, triviaId, questionIds) => {
        return axios({
            method: 'put',
            url: buildUrl(idGroup, `/trivia/core/${triviaId}/questions/reorder`),
            headers: { ...headers(idEmpresa, idUsuario), 'Content-Type': 'application/json' },
            data: { questionIds }
        });
    },

    postCreateSession: (idGroup, idEmpresa, idUsuario, triviaId) => {
        return axios({
            method: 'post',
            url: buildUrl(idGroup, `/trivia/core/${triviaId}/sessions`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    getSessionLobby: (idGroup, idEmpresa, idUsuario, sessionId) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, `/trivia/core/sessions/${sessionId}/lobby`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    postStartSession: (idGroup, idEmpresa, idUsuario, sessionId) => {
        return axios({
            method: 'post',
            url: buildUrl(idGroup, `/trivia/core/sessions/${sessionId}/start`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    postCancelSession: (idGroup, idEmpresa, idUsuario, sessionId) => {
        return axios({
            method: 'post',
            url: buildUrl(idGroup, `/trivia/core/sessions/${sessionId}/cancel`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    getSessionLive: (idGroup, idEmpresa, idUsuario, sessionId) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, `/trivia/core/sessions/${sessionId}/live`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    postNextSession: (idGroup, idEmpresa, idUsuario, sessionId) => {
        return axios({
            method: 'post',
            url: buildUrl(idGroup, `/trivia/core/sessions/${sessionId}/next`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    postEndSession: (idGroup, idEmpresa, idUsuario, sessionId) => {
        return axios({
            method: 'post',
            url: buildUrl(idGroup, `/trivia/core/sessions/${sessionId}/end`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    getTriviaSessions: (idGroup, idEmpresa, idUsuario, triviaId, params = {}) => {
        const query = new URLSearchParams();
        if (params.page !== undefined) query.append('page', params.page);
        if (params.size !== undefined) query.append('size', params.size);
        const queryString = query.toString();
        const url = buildUrl(idGroup, `/trivia/core/${triviaId}/sessions${queryString ? '?' + queryString : ''}`);

        return axios({
            method: 'get',
            url,
            headers: headers(idEmpresa, idUsuario)
        });
    },

    getSessionById: (idGroup, idEmpresa, idUsuario, sessionId) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, `/trivia/core/sessions/${sessionId}`),
            headers: headers(idEmpresa, idUsuario)
        });
    },

    getSessionSummary: (idGroup, idEmpresa, idUsuario, sessionId) => {
        return axios({
            method: 'get',
            url: buildUrl(idGroup, `/trivia/core/sessions/${sessionId}/summary`),
            headers: headers(idEmpresa, idUsuario)
        });
    }
};
